import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TableSortLabel, Chip,
} from '@mui/material';
import CategoryDetailChart from '../components/analytics/CategoryDetailChart';
import OutlierCard from '../components/analytics/OutlierCard';
import {
  getCategoryBreakdown,
  getTrends,
  getOutliers,
  getAvailableYears,
} from '../services/analytics';

const formatCurrency = (value) =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTH_ABBREVS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function Analytics() {
  const [year, setYear] = useState(null);
  const [years, setYears] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trends, setTrends] = useState([]);
  const [outliers, setOutliers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('total');
  const [sortDir, setSortDir] = useState('desc');

  const loadData = useCallback(async (selectedYear) => {
    setLoading(true);
    setError(null);
    try {
      const [catData, trendData, outlierData] = await Promise.all([
        getCategoryBreakdown(selectedYear),
        getTrends(selectedYear),
        getOutliers(selectedYear),
      ]);
      setCategories(catData);
      setTrends(trendData);
      setOutliers(outlierData);
    } catch (err) {
      if (err.response?.status !== 401) {
        setError('Failed to load analytics data.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    async function init() {
      try {
        const availableYears = await getAvailableYears();
        setYears(availableYears);
        const defaultYear = availableYears.length > 0 ? availableYears[0] : null;
        setYear(defaultYear);
        await loadData(defaultYear);
      } catch (err) {
        if (err.response?.status !== 401) {
          setLoading(false);
        }
      }
    }
    init();
  }, [loadData]);

  const handleYearChange = (e) => {
    const newYear = e.target.value || null;
    setYear(newYear);
    setSelectedCategory(null);
    loadData(newYear);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Determine which months are present in data
  const allMonthKeys = new Set();
  categories.forEach((cat) => {
    Object.keys(cat.monthly || {}).forEach((k) => allMonthKeys.add(k));
  });
  const sortedMonthKeys = [...allMonthKeys].sort();

  // Sort categories
  const sortedCategories = [...categories].sort((a, b) => {
    let aVal, bVal;
    if (sortField === 'category') {
      aVal = a.category.toLowerCase();
      bVal = b.category.toLowerCase();
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    aVal = a[sortField] || 0;
    bVal = b[sortField] || 0;
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (categories.length === 0) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 2 }}>Analytics</Typography>
        <Alert severity="info">No spending data available. Upload budget files first.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4">Category Analytics</Typography>
        {years.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select value={year || ''} label="Year" onChange={handleYearChange}>
              <MenuItem value="">All Years</MenuItem>
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Category drill-down chart */}
      {selectedCategory && (
        <Box sx={{ mb: 3 }}>
          <CategoryDetailChart
            category={selectedCategory}
            onClose={() => setSelectedCategory(null)}
          />
        </Box>
      )}

      {/* Category table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'category'}
                  direction={sortField === 'category' ? sortDir : 'asc'}
                  onClick={() => handleSort('category')}
                >
                  Category
                </TableSortLabel>
              </TableCell>
              {sortedMonthKeys.map((mk) => {
                const [, m] = mk.split('-');
                return (
                  <TableCell key={mk} align="right" sx={{ minWidth: 80 }}>
                    {MONTH_ABBREVS[parseInt(m, 10) - 1]}
                  </TableCell>
                );
              })}
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'average_monthly'}
                  direction={sortField === 'average_monthly' ? sortDir : 'desc'}
                  onClick={() => handleSort('average_monthly')}
                >
                  Avg/Mo
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'total'}
                  direction={sortField === 'total' ? sortDir : 'desc'}
                  onClick={() => handleSort('total')}
                >
                  Total
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedCategories.map((cat) => (
              <TableRow
                key={cat.category}
                hover
                onClick={() => setSelectedCategory(cat)}
                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.50' } }}
              >
                <TableCell>
                  <Chip label={cat.category} size="small" variant="outlined" />
                </TableCell>
                {sortedMonthKeys.map((mk) => {
                  const val = cat.monthly[mk]?.total;
                  return (
                    <TableCell key={mk} align="right" sx={{ fontSize: '0.8rem' }}>
                      {val != null ? formatCurrency(val) : '-'}
                    </TableCell>
                  );
                })}
                <TableCell align="right" sx={{ fontWeight: 500 }}>
                  {formatCurrency(cat.average_monthly)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatCurrency(cat.total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Trends & Outliers */}
      <OutlierCard trends={trends} outliers={outliers} title="Trends & Outliers" />
    </Box>
  );
}
